/**
 * Server functions for the `/admin/users` operations screen. Each one is admin
 * gated and delegates straight to `~/lib/server/admin-users`; dates cross the
 * boundary as ISO strings so the client never has to guess at the shape.
 *
 * Aliases: admin users server functions, delete user immediately, schedule
 * account deletion, cancel account deletion.
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { permissionMiddleware } from "~/lib/permission-middleware";
import {
  ADMIN_USER_SORTS,
  type AdminUserSort,
  type GrantRoleResult,
  type RemoveRoleResult,
} from "~/lib/shared/admin-users";
import { roleKeySchema, type RoleKey } from "~/lib/shared/roles";
import { SORT_DIRECTIONS, type SortDirection } from "~/lib/shared/sort";

export interface AdminUserDto {
  readonly id: string;
  readonly email: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly clerkUserId: string | null;
  readonly createdAt: string | null;
  readonly scheduledForDeletionAt: string | null;
  readonly coach: {
    readonly id: string;
    readonly username: string;
    readonly fullName: string;
  } | null;
  /** Stored role keys, defined roles first; see `roleLabel` for display. */
  readonly roles: readonly string[];
  /** The row is the signed-in admin. */
  readonly isSelf: boolean;
}

export interface AdminUserListDto {
  readonly rows: readonly AdminUserDto[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export type ImmediateDeletionDto =
  | { readonly status: "deleted"; readonly clerkAccountAlreadyAbsent: boolean }
  | { readonly status: "user_not_found" }
  | { readonly status: "last_admin" }
  | { readonly status: "workflow_cancel_failed"; readonly error: string }
  | { readonly status: "workflow_service_unavailable" }
  | { readonly status: "clerk_delete_failed"; readonly error: string }
  | {
      readonly status: "database_delete_failed";
      readonly userId: string;
      readonly clerkUserId: string | null;
      readonly error: string;
    };

export interface AccountDeletionActionDto {
  readonly success: boolean;
  readonly error?: string;
  readonly unsupported?: boolean;
  /**
   * True when the action armed nothing but a deletion recorded earlier is still
   * pending. Reporting "nothing was scheduled" without checking this tells the
   * operator a deletion is off while the original job still runs.
   */
  readonly existingDeletionPending?: boolean;
  /** ISO date of that still-pending deletion, when it is known. */
  readonly existingDeletionAt?: string;
}

export interface AdminUserListParams {
  readonly search: string;
  readonly page: number;
  readonly pageSize: number;
  readonly role: RoleKey | undefined;
  readonly sort: AdminUserSort;
  readonly direction: SortDirection;
}

export const adminUserQueryKeys = {
  all: ["admin-users"] as const,
  list: (params: AdminUserListParams) =>
    [
      "admin-users",
      "list",
      params.search,
      params.page,
      params.pageSize,
      params.role ?? null,
      params.sort,
      params.direction,
    ] as const,
};

const listInput = z.object({
  search: z.string().default(""),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(25),
  role: roleKeySchema.optional(),
  sort: z.enum(ADMIN_USER_SORTS).default("joined"),
  direction: z.enum(SORT_DIRECTIONS).default("desc"),
});

const userIdInput = z.object({ userId: z.string().uuid() });

export const listAdminUsersFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("users:read")])
  .validator(listInput)
  .handler(async ({ data, context }): Promise<AdminUserListDto> => {
    const { listAdminUsers } = await import("~/lib/server/admin-users");

    const result = await listAdminUsers(data);

    return {
      rows: result.rows.map((row) => ({
        id: row.id,
        email: row.email,
        firstName: row.firstName,
        lastName: row.lastName,
        clerkUserId: row.clerkUserId,
        createdAt: row.createdAt?.toISOString() ?? null,
        scheduledForDeletionAt:
          row.scheduledForDeletionAt?.toISOString() ?? null,
        coach: row.coach,
        roles: row.roles,
        isSelf: row.id === context.user.id,
      })),
      total: result.total,
      page: result.page,
      pageSize: result.pageSize,
    };
  });

export function adminUsersQueryOptions(params: AdminUserListParams) {
  return queryOptions({
    queryKey: adminUserQueryKeys.list(params),
    queryFn: () => listAdminUsersFn({ data: params }),
  });
}

/** Starts the normal grace period for a user who is not already scheduled. */
export const scheduleUserDeletionFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("users:write")])
  .validator(userIdInput)
  .handler(async ({ data }): Promise<AccountDeletionActionDto> => {
    const { scheduleAccountDeletionForUser } =
      await import("~/lib/server/admin-users");

    return scheduleAccountDeletionForUser(data.userId);
  });

export const cancelUserDeletionFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("users:write")])
  .validator(userIdInput)
  .handler(async ({ data }): Promise<AccountDeletionActionDto> => {
    const { cancelAccountDeletionForUser } =
      await import("~/lib/server/admin-users");

    return cancelAccountDeletionForUser(data.userId);
  });

/** Grace-period bypass: terminate workflow, delete from Clerk, delete our row. */
export const deleteUserImmediatelyFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("users:write")])
  .validator(userIdInput)
  .handler(async ({ data }): Promise<ImmediateDeletionDto> => {
    const { deleteUserImmediately } = await import("~/lib/server/admin-users");

    return deleteUserImmediately(data.userId);
  });

export interface UserRoleGrantDto {
  readonly key: string;
  readonly grantedAt: string;
  readonly grantedBy: { readonly id: string; readonly name: string } | null;
}

export const userRolesQueryKey = (userId: string) =>
  ["admin-users", "roles", userId] as const;

/** One person's roles with who gave each; null when the user is gone. */
export const getUserRolesFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("roles:write")])
  .validator(userIdInput)
  .handler(async ({ data }): Promise<readonly UserRoleGrantDto[] | null> => {
    const { getUserRoles } = await import("~/lib/server/admin-user-roles");

    const record = await getUserRoles(data.userId);
    if (record === null) return null;
    return record.roles.map((role) => ({
      key: role.key,
      grantedAt: role.grantedAt.toISOString(),
      grantedBy: role.grantedBy,
    }));
  });

export function userRolesQueryOptions(userId: string) {
  return queryOptions({
    queryKey: userRolesQueryKey(userId),
    queryFn: () => getUserRolesFn({ data: { userId } }),
  });
}

/** Gives a role code defines. Only `roles:write` (Admin) may. */
export const grantUserRoleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("roles:write")])
  .validator(z.object({ userId: z.string().uuid(), role: roleKeySchema }))
  .handler(async ({ data, context }): Promise<GrantRoleResult> => {
    const { grantUserRole } = await import("~/lib/server/admin-user-roles");

    return grantUserRole({
      actorUserId: context.user.id,
      userId: data.userId,
      role: data.role,
    });
  });

/** Removes a stored role, including one code no longer defines. */
export const removeUserRoleFn = createServerFn({ method: "POST" })
  .middleware([permissionMiddleware("roles:write")])
  .validator(
    z.object({ userId: z.string().uuid(), role: z.string().min(1).max(100) }),
  )
  .handler(async ({ data, context }): Promise<RemoveRoleResult> => {
    const { removeUserRole } = await import("~/lib/server/admin-user-roles");

    return removeUserRole({
      actorUserId: context.user.id,
      userId: data.userId,
      role: data.role,
    });
  });
